/**
 * Usage:
 *
 *   data-sw-input-autocomplete
 *
 *   data-sw-input-autocomplete-values="<JSON array of autocomplete values>"
 *
 *   data-sw-input-autocomplete-url="<url endpoint for JSON autocomplete values>"
 *
 *   data-sw-input-autocomplete-append-to="<selector to append the autocomplete elements to>"
 *
 *   data-sw-input-autocomplete-tracking-services="<optional tracking services>"
 *
 *   data-sw-input-autocomplete-tracking-event="<optional tracking event>"
 *
 *   data-sw-input-autocomplete-tracking-link-type="<optional tracking link type>"
 *    The linkId will be the value of term selected
 *
 *   JSON autocomplete values may be an array of strings:
 *    ['first suggestion', 'second suggestion', ...]
 *   or it may be an array of objects with metadata for facet/category and group
 *    [
 *    {
 *      "value": "<suggestion value>",
 *      "searchTrees": [
 *        {
 *          "url": "< optional url that will be used for the query >",
 *          "group": "< optional group value to avoid suggesting values from the same group>",
 *          "searchTree": [
 *            "<array for display - i.e. 'in category > subcategory'"
 *          ],
 *          "category": {
 *            "title": "< category title - if a suggestion value is currently in the input, results will be scoped to the same category >",
 *            "url": "< optional url that will be used for the query if this category is selected >"
 *          }
 *        },
 *        ....
 *        ]
 *    },
 *    ....
 *   ]
 *
 */

var $   = require('js/sw-jquery/sw-jquery');
var _ = require('underscore');
var menuTemplate = require('js/sw-jquery/view-builders/sw-input-autocomplete/tpl.autocomplete-menu.mustache');
var menuItems = require('js/sw-jquery/view-builders/sw-input-autocomplete/tpl.autocomplete-items.mustache');

var INPUT_AUTOCOMPLETE_VALUES = 'data-sw-input-autocomplete-values';
var INPUT_AUTOCOMPLETE_URL = 'data-sw-input-autocomplete-url';
var INPUT_AUTOCOMPLETE_APPEND_TO = 'data-sw-input-autocomplete-append-to';
var INPUT_AUTOCOMPLETE_TRACKING_SERVICES = 'data-sw-input-autocomplete-tracking-services';
var INPUT_AUTOCOMPLETE_TRACKING_EVENT = 'data-sw-input-autocomplete-tracking-event';
var INPUT_AUTOCOMPLETE_TRACKING_LINK_TYPE = 'data-sw-input-autocomplete-tracking-link-type';
var RIGHT_SIDE_NAV_LINKS = 'data-sw-dms-nav-right-side-links';

var INPUT_AUTOCOMPLETE_OPEN_CLASS = 'sw-input-autocomplete--open';
var INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS = 'sw-input-autocomplete__item--selected';
var NAV_AUTOCOMPLETE_OPEN = 'sw-dms-nav--autocomplete-open';
var INPUT_AUTOCOMPLETE_MENU = 'data-sw-input-autocomplete-menu';
var INPUT_AUTOCOMPLETE_ITEM = 'data-sw-input-autocomplete-item';
var INPUT_AUTOCOMPLETE_ITEM_SELECTOR = '[' + INPUT_AUTOCOMPLETE_ITEM + ']';

/**
 * Replace all any matches in the input with
 * the suggested term so that the capitalization
 * will match the items in the suggest list
 *
 * @param inputArray
 * @param sourceItems
 * @return string
 */
var replaceValuesWithSearchTerms = function replacevaluesWithSearchTerms(inputArray, sourceItems) {
  return _.reduce(inputArray, function (inputValuesCleaned, word) {
    var match = _.find(sourceItems, function(item) {
      var itemValue = (_.isObject(item) ? item.value : item).toString().toLowerCase();
      return itemValue.toLowerCase() === word;
    });
    if (match) {
      inputValuesCleaned.push((match.value || match).toString());
    } else {
      inputValuesCleaned.push(word);
    }
    return inputValuesCleaned;
  }, []).join(' ');
};

/**
 * Create an object of { categories: group || true } from the input value.
 * If an suggest item is in the input, get it's categories and group
 * into the object.
 *
 * @param words
 * @param sourceItems
 * @return {*}
 */
var getCurrentCategoriesAndGroups = function getCurrentCategoriesAndGroups(words, sourceItems) {
  var searchPhrase = words.join(' ');
  return _.reduce(sourceItems, function (getCurrentCategoriesAndGroups, item) {
    if (!_.isObject(item) || !item.searchTrees || !item.value) {
      return getCurrentCategoriesAndGroups;
    }
    var itemValueRegex = new RegExp('\\b' + item.value.toString().toLowerCase() + '\\b');
    if (itemValueRegex.test(searchPhrase)) {
      getCurrentCategoriesAndGroups = _.extend(getCurrentCategoriesAndGroups,
        _.reduce(item.searchTrees, function(categoriesAndGroups, searchTree) {
          if (!searchTree.category) {
            return categoriesAndGroups;
          }
          categoriesAndGroups[searchTree.category.title] = searchTree.group || true;
          return categoriesAndGroups;
        }, {})
      );
    }
    return getCurrentCategoriesAndGroups;
  }, {});
};

/**
 * Get any matches from the source for
 * the last word (partial word) in the input.
 *
 * If the currentCategoriesAndGroups object has categories and groups,
 * only use items that are in the same category but a different group or just
 * add if no group is defined.
 *
 *
 * @param inputValue
 * @param sourceItems
 * @return {*}
 */
var findWordMatches = function findWordMatches(inputValue, sourceItems) {
  var inputArray = inputValue.split(' ');
  var completeWords = inputArray.slice(0, inputArray.length - 1);
  var currentCategoriesAndGroups = false;
  if (completeWords.length !== 0) {
    currentCategoriesAndGroups = getCurrentCategoriesAndGroups(completeWords, sourceItems);
  }
  var lastWord = _.last(inputArray);
  // Do not try to match if there is nothing typed for the last word and no matches exist in the current input
  if (lastWord.length === 0 && _.keys(currentCategoriesAndGroups).length === 0) {
    return false;
  }
  return _.reduce(sourceItems, function (matchItems, item) {
    var itemValue = (_.isObject(item) ? item.value : item).toString().toLowerCase();
    var itemValueRegex = new RegExp('\\b' + itemValue + '\\b');

    if (itemValue.indexOf(lastWord) === 0 && !itemValueRegex.test(inputValue)) {
      if (!currentCategoriesAndGroups || _.keys(currentCategoriesAndGroups).length === 0) {
        matchItems.push(item);
      } else {
        var searchTreeMatches = _.reduce(item.searchTrees, function(searchTreeMatches, searchTree) {
          var category = searchTree.category.title || searchTree.category;
          if (currentCategoriesAndGroups[category] && (
            currentCategoriesAndGroups[category] === true ||
            currentCategoriesAndGroups[category] !== searchTree.group)) {
            searchTreeMatches.push(searchTree);
          }
          return searchTreeMatches;
        }, []);
        if (searchTreeMatches.length > 0) {
          item.searchTrees = searchTreeMatches;
          matchItems.push(item);
        }
      }
    }
    return matchItems;
  }, []);
};

/**
 * Generate the label parts for the autocomplete
 * which will make the suggested text grey.
 *
 * @param inputValueCleaned
 * @param autocompleteValue
 * @return {Array}
 */
var getLabelParts = function getLabelParts(inputValueCleaned, autocompleteValue) {
  var labelParts = [];
  var inputValueLength = inputValueCleaned.length;
  var firstPart = autocompleteValue.slice(0, inputValueLength);
  if (firstPart.length > 0) {
    labelParts.push({
      labelPart: firstPart,
      isSearchMatchText: true
    });
  }
  var lastPart = autocompleteValue.slice(inputValueLength);
  if (lastPart.length > 0) {
    labelParts.push({labelPart: lastPart});
  }
  return labelParts;
};

/**
 * Generate the match items for the autocomplete
 * element.
 * 
 * @param inputValue
 * @param sourceItems
 * @param maxItems
 * @param maxSubItems
 * @return {*}
 */
var getMatchItems = function getMatchItems(inputValue, sourceItems, maxItems, maxSubItems) {
  var inputArray = inputValue.split(' ');
  var inputValueCleaned = replaceValuesWithSearchTerms(inputArray, sourceItems);
  var lastWord = _.last(inputArray);
  var lastWordLength = lastWord.length;
  var inputValueLength = inputValueCleaned.length;

  var isFirstItem = true;

  var wordMatches = findWordMatches(inputValue, sourceItems);
  return _.reduce(wordMatches, function (matchItems, item) {
    if (matchItems.length >= maxItems) {
      return matchItems;
    }
    var itemValue = _.isObject(item) ? item.value.toString() : item.toString();
    var autocompleteLastWord = '';
    // If the last word is equal to the item, replace it with the item value
    if (lastWord === itemValue.toLowerCase().slice(0, lastWordLength)) {
      autocompleteLastWord = itemValue;
    }
    var autocompleteValue = inputValueCleaned.slice(0, inputValueLength - lastWordLength) + autocompleteLastWord;
    matchItems.push({
      value: autocompleteValue,
      labelParts: getLabelParts(inputValueCleaned, autocompleteValue)
    });
    if (item.searchTrees && item.searchTrees.length > 0 && isFirstItem) {
      isFirstItem = false;
      matchItems =  matchItems.concat(_.reduce(item.searchTrees, function(matchSubItems, searchTreeItem) {
        if (matchSubItems.length >= maxSubItems) {
          return matchSubItems;
        }
        if (searchTreeItem.searchTree) {
          var label = searchTreeItem.searchTree.join(' > ');
          matchSubItems.push({
            value: autocompleteValue,
            label: label,
            url: searchTreeItem.url,
            isSubItem: true
          });
        }
        return matchSubItems;
      }, []));
    }
    return matchItems;
  }, []);
};

module.exports = function ($fragment)
{
  $fragment.swFindWithSelf('[data-sw-input-autocomplete]').each(function () {
    var $el = $(this);
    var elId = $el.data('swInputAutocomplete');
    var autocompleteValues = $el.attr(INPUT_AUTOCOMPLETE_VALUES);
    var autocompleteUrl = $el.attr(INPUT_AUTOCOMPLETE_URL);

    if (!autocompleteValues && !autocompleteUrl) {
      return;
    }

    var autoCompleteAppendTo = $el.attr(INPUT_AUTOCOMPLETE_APPEND_TO);
    var autocompleteOptions = {
      minLength: 1,
      // If a appendTo element is defined, use it
      appendTo: autoCompleteAppendTo ? $(autoCompleteAppendTo) : $el,
      maxItems: 6,
      maxSubItems: 3,
      trackingServices: $el.attr(INPUT_AUTOCOMPLETE_TRACKING_SERVICES),
      trackingEventType: $el.attr(INPUT_AUTOCOMPLETE_TRACKING_EVENT),
      trackingLinkType: $el.attr(INPUT_AUTOCOMPLETE_TRACKING_LINK_TYPE)
    };

    var $autocompleteEl;

    var $rightSideNavLinks;

    /**
     * Closes autocomplete
     */
    var closeAutocomplete = function closeAutocomplete() {
      $autocompleteEl.removeClass(INPUT_AUTOCOMPLETE_OPEN_CLASS);
      $rightSideNavLinks.removeClass(NAV_AUTOCOMPLETE_OPEN);
      $el.trigger('sw-input-autocomplete-close');
    };

    /**
     * Open, or update autocomplete
     */
    var activateAutocomplete = function activateAutocomplete() {
      var inputValue = $el.val().toLowerCase();
      if (inputValue.length < autocompleteOptions.minLength || $autocompleteEl.is(":hidden")) {
        if ($autocompleteEl.hasClass(INPUT_AUTOCOMPLETE_OPEN_CLASS)) {
          closeAutocomplete();
        }
        return;
      }

      var matchItems = getMatchItems(inputValue, autocompleteOptions.source,
        autocompleteOptions.maxItems, autocompleteOptions.maxSubItems);
      if (!matchItems || matchItems.length === 0) {
        if ($autocompleteEl.hasClass(INPUT_AUTOCOMPLETE_OPEN_CLASS)) {
          closeAutocomplete();
        }
        return;
      }
      var mustacheView = {
        menuItems: matchItems
      };
      $autocompleteEl.html(menuItems.render(mustacheView));
      if (!$autocompleteEl.hasClass(INPUT_AUTOCOMPLETE_OPEN_CLASS)) {
        $autocompleteEl.addClass(INPUT_AUTOCOMPLETE_OPEN_CLASS);
        $rightSideNavLinks.addClass(NAV_AUTOCOMPLETE_OPEN);
        $el.trigger('sw-input-autocomplete-open');
      }
    };

    /**
     * Handles up or down arrows
     *
     * @param direction
     */
    var keyboardNavigationArrows = function keyboardNavigationArrows(direction) {
      var $selectedItem = $autocompleteEl.find('.' + INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS);
      $autocompleteEl.find(INPUT_AUTOCOMPLETE_ITEM_SELECTOR).removeClass(INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS);
      var $nextItem;
      if (direction === 'down') {
        $nextItem = $selectedItem.next(INPUT_AUTOCOMPLETE_ITEM_SELECTOR);
      } else {
        $nextItem = $selectedItem.prev(INPUT_AUTOCOMPLETE_ITEM_SELECTOR);
      }
      if ($selectedItem.length === 0 || $nextItem.length === 0) {
        if (direction === 'down') {
          $autocompleteEl.children(INPUT_AUTOCOMPLETE_ITEM_SELECTOR).first().addClass(INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS);
        } else {
          $autocompleteEl.children(INPUT_AUTOCOMPLETE_ITEM_SELECTOR).last().addClass(INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS);
        }
      } else {
        $nextItem.addClass(INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS);
      }
    };

    /**
     *
     * @param searchQuery
     * @param url
     */
    var setSearchQueryAndSearch = function setSearchQueryAndSearch(searchQuery, url) {
      if (!searchQuery) {
        return;
      }
      var $form = $($el[0].form);
      if (autocompleteOptions.trackingEventType) {
        var trackingData = {
          linkId: searchQuery,
          linkType: autocompleteOptions.trackingLinkType
        };
        var targetUrl = url || $form.attr('action');
        if (targetUrl) {
          var urlSections = _.without(targetUrl.split('/'), '');
          trackingData = _.extend(trackingData,
            _.object(['targetSection', 'targetSubSection', 'targetPageIdentifier'], urlSections));
        }
      }
      $el.val(searchQuery).focus();
      closeAutocomplete();
      if (url) {
        $form.attr('action', url);
      }
      $form.submit();
    };

    /**
     * Handle key press events
     *
     * @param e
     */
    var keyboardNavigation = function keyboardNavigation(e) {
      switch (e.keyCode) {
        case 40: // Down
          if (!$autocompleteEl.hasClass(INPUT_AUTOCOMPLETE_OPEN_CLASS)) {
            activateAutocomplete();
          }
          keyboardNavigationArrows('down');
          break;
        case 38: // Up
          keyboardNavigationArrows('up');
          break;
        case 13: // Enter
        case 9: // Tab
          var $selectedItem = $autocompleteEl.find('.' + INPUT_AUTOCOMPLETE_ITEM_SELECTED_CLASS);
          if ($selectedItem.length === 0) {
            return;
          }
          var itemLabel = $selectedItem.attr(INPUT_AUTOCOMPLETE_ITEM);
          // For Tab key
          if (e.keyCode === 9) {
            if (itemLabel) {
              $el.val(itemLabel);
              activateAutocomplete();
            }
          // For Enter key
          } else {
            if (itemLabel) {
              setSearchQueryAndSearch(itemLabel, $selectedItem.attr('href'));
            } else {
              $selectedItem.click();
            }
          }
          break;
        case 27: // Escape
          // Escape closes autocomplete
          closeAutocomplete();
          break;
        default:
          // return if any other key so prevent default does not get triggered
          return;
      }
      e.preventDefault();
    };

    /**
     * Initialize autocomplete
     *
     * @param source
     */
    var initAutocomplete = function initAutocomplete(source) {
      autocompleteOptions.source = source;
      autocompleteOptions.appendTo.append(menuTemplate.render({uniqId: elId}));
      $autocompleteEl = $('[' + INPUT_AUTOCOMPLETE_MENU + '=' + elId +']');
      $rightSideNavLinks = $('[' + RIGHT_SIDE_NAV_LINKS + ']');

      $el.on('focus input', activateAutocomplete);
      $el.on('focusout', closeAutocomplete);
      $el.keydown(keyboardNavigation);
      $autocompleteEl.on('click', INPUT_AUTOCOMPLETE_ITEM_SELECTOR, function(e) {
        var $target = $(e.currentTarget);
        var itemLabel = $target.attr(INPUT_AUTOCOMPLETE_ITEM);
        if (itemLabel) {
          e.preventDefault();
          setSearchQueryAndSearch(itemLabel, $target.attr('href'));
        }
      });
    };

    if (autocompleteValues) {
      var source = JSON.parse(autocompleteValues);
      initAutocomplete(source);
    } else {
      // Wait until the user interacts with the input before making the ajax request for the autocomplete source
      $el.one('focus', function() {
        $.ajax({
          url: autocompleteUrl,
          dataType: 'json',
          success: function (source) {
            initAutocomplete(source);
          }
        });
      });
    }
  });
};
